import { MimeType } from 'vet-bones/constants';

import { databasesStardogRequestDispatchers } from 'src/actions/databases/actionCreators';
import { DatabasesActionType } from 'src/actions/databases/actionTypes';
import { PublishProjectResponse } from 'src/actions/projects/actionCreators';
import {
  CreationType,
  ModelInfo,
} from 'src/containers/PublishProjectDrawer/types';
import { NamedGraph } from 'src/store/state/databases';
import { Project } from 'src/store/state/projects';
import * as copy from 'src/templates/copy';
import { exportModel } from 'src/utils/export/model';
import { stringifyProjectMetadata } from 'src/utils/stringify/stringifyProjectMetadata';

export const publishModel = async ({
  databaseId,
  danglingNamedGraph,
  project,
  modelInfo,
  namedGraphs,
  projectModelIds,
  publishedDate,
}: {
  databaseId: string;
  /** can be falsey */
  danglingNamedGraph: string;
  project: Project;
  modelInfo: ModelInfo;
  namedGraphs: Record<string, NamedGraph>;
  projectModelIds?: Set<string>;
  publishedDate: number;
}): Promise<PublishProjectResponse> => {
  const isReplace = modelInfo.creationType === CreationType.REPLACE;
  const modelCopy = isReplace
    ? copy.notifications.publishProject.model.replace
    : copy.notifications.publishProject.model.create;

  // Begin a transaction
  const transactionResponse =
    await databasesStardogRequestDispatchers.beginDatabaseTransaction(
      databaseId
    );

  const transactionId = transactionResponse.payload.response?.body;

  if (
    transactionResponse.type ===
      DatabasesActionType.BEGIN_DATABASE_TRANSACTION_FAILURE ||
    !transactionId
  ) {
    return {
      success: false,
      action: transactionResponse,
      actionDescription: copy.notifications.database.beginTransaction,
      message: modelCopy.failure,
    };
  }

  const runWithTransaction = async (): Promise<PublishProjectResponse> => {
    const namedGraph = modelInfo.namedGraphIri;

    // Clear the named graph on the server,
    // which can differ from the current named graph if the user chose Replace
    // and edits the named graph iri.
    if (isReplace) {
      // `danglingNamedGraph` is set to the previously existing graph on the server
      // ONLY if it needs to be deleted (if the graph also belongs to another model it is not),
      // otherwise use `namedGraph`, which is what the user has selected.
      const clearNamedGraph = danglingNamedGraph || namedGraph;
      // in the `namedGraph` case, the graph might not exist yet if the user
      // edited the named graph field to a new iri
      if (namedGraphs[clearNamedGraph]) {
        const clearGraphResponse =
          await databasesStardogRequestDispatchers.clearDatabaseNamedGraph({
            databaseId,
            transactionId,
            namedGraph: clearNamedGraph,
          });

        if (
          clearGraphResponse.type ===
          DatabasesActionType.CLEAR_DATABASE_NAMED_GRAPH_FAILURE
        ) {
          return {
            success: false,
            action: clearGraphResponse,
            actionDescription: copy.notifications.database.clearNamedGraph,
            message: modelCopy.failure,
          };
        }
      }
    }

    // Publish the model
    const { contents, mimeType } = exportModel({
      name: project.name,
      model: project.diagram.model,
      projectModelIds,
      projectPropertyType: project.propertyType,
      targetModelId: project.targetModelId,
    });
    const addDataResponse =
      await databasesStardogRequestDispatchers.addDataToDatabase({
        databaseId,
        transactionId,
        contents,
        mimeType,
        namedGraph,
      });

    if (
      addDataResponse.type === DatabasesActionType.ADD_DATA_TO_DATABASE_FAILURE
    ) {
      return {
        success: false,
        action: addDataResponse,
        actionDescription: copy.notifications.database.addData,
        message: modelCopy.failure,
      };
    }

    // Publish the model metadata
    const model = project.modelsById[modelInfo.projectModelId];
    const metadata = stringifyProjectMetadata({
      namedGraph,
      label: project.name,
      publishedDate,
      voiceboxInstructions: model?.voiceboxInstructions,
    });
    const addMetadataResponse =
      await databasesStardogRequestDispatchers.addDataToDatabase({
        databaseId,
        transactionId,
        contents: metadata,
        mimeType: MimeType.TRIG,
      });
    if (
      addMetadataResponse.type ===
      DatabasesActionType.ADD_DATA_TO_DATABASE_FAILURE
    ) {
      return {
        success: false,
        action: addMetadataResponse,
        actionDescription: copy.notifications.database.addData,
        message: modelCopy.failure,
      };
    }

    // Commit the transaction
    const commitResponse =
      await databasesStardogRequestDispatchers.commitDatabaseTransaction(
        databaseId,
        transactionId
      );

    if (
      commitResponse.type ===
      DatabasesActionType.COMMIT_DATABASE_TRANSACTION_FAILURE
    ) {
      return {
        success: false,
        action: commitResponse,
        actionDescription: copy.notifications.database.commitTransaction,
        message: modelCopy.failure,
      };
    }

    return {
      success: true,
      action: commitResponse,
      message: modelCopy.success(modelInfo.modelName),
    };
  };

  const result = await runWithTransaction();
  if (!result.success) {
    await databasesStardogRequestDispatchers.rollbackDatabaseTransaction(
      databaseId,
      transactionId
    );
  }

  return result;
};
